/**
 * Warteblasen im Verlauf — EIN Baustein, zwei Varianten. (16.10.2026)
 *
 * Martin, 17:58, über die Warteblase des Schmieds: „Ich möchte gern, dass du die Sprechblase von Vespera auch so schön anpasst,
 * wie du es bei dir gemacht hast.“ Beide Blasen teilen darum denselben Aufbau: oben (falls bekannt) der Name in Kapitälchen,
 * darunter eine Zeile aus drei sanft tippenden Pünktchen und einem kurzen Statustext.
 *
 *   - Schmied (lib/schmied-warteblase.ts): eigene Klassen, KEIN `.bubble` — sie ist kein Beitrag und steht per CSS `order` zuletzt.
 *   - Vespera (hier, `baueVesperaWarteblase`): eine echte `.bubble.bot.is-pending` an der Stelle ihrer künftigen Antwort
 *     (insertOrdered am Anker des Zuges, app.ts). `.is-pending` heißt „flüchtig“ — lib/schritte.ts zählt sie darum nicht als
 *     Vorgänger eines Zuges. Die Antwort ersetzt sie an genau dieser Stelle.
 *
 * ⚠️ Der Text sagt nur, was belegt ist: den Stand, den der Aufrufer kennt (gesendet, Werkzeug läuft, …). Nie mehr.
 * ⚠️ Nur textContent, kein HTML aus Zeichenketten, keine Stile im Code — die Optik steht in style.css (Abschnitt „Warteblasen“).
 * ⚠️ Barrierefrei: `role="status"`, höflich, als Ganzes; die Pünktchen sind `aria-hidden` (nicht vorgelesen).
 * ⚠️ Kein Scrollen hier: wer einhängt, ruft den Folge-Weg des Verlaufs (lib/chat-folgen.ts, lib/scroll-ruhe.ts).
 */

/** Die Klasse der Vespera-Warteblase (zusätzlich zu `bubble bot is-pending`). */
export const VESPERA_WARTEBLASE_KLASSE = "vespera-warteblase";

/**
 * Obergrenze: so lange steht eine Vespera-Warteblase höchstens. Die Ströme beenden das Warten viel früher (Antwort, Ende,
 * Fehler, Stille-Wächter nach 60 s ohne Byte — lib/chat-http.ts, lib/ptt-haenger.ts); das hier ist nur das letzte Netz,
 * damit sie nie hängen bleibt, falls ein Weg das Aufräumen verliert.
 */
export const VESPERA_WARTEN_HOECHSTENS_MS = 10 * 60_000;

/** Der Text, solange nichts Genaueres bekannt ist. */
export const WARTE_STANDARD_TEXT = "Anfrage wird bearbeitet …";

export interface WarteZeile {
  zeile: HTMLElement;
  text: HTMLElement;
}

/** Die drei Pünktchen (aria-hidden) — eine Komponente für alle Wartezustände (CSS `.warte-punkte`, Farbe `--warte-akzent`). */
export function baueWartePunkte(doc: Document, klasse = "warte-punkte"): HTMLElement {
  const punkte = doc.createElement("span");
  punkte.className = klasse;
  punkte.setAttribute("aria-hidden", "true");
  for (let i = 0; i < 3; i++) punkte.appendChild(doc.createElement("span"));
  return punkte;
}

/**
 * Die gemeinsame Zeile: drei Pünktchen (aria-hidden) + Statustext. `praefix` gibt den Teilen zusätzlich die Klassen
 * `<praefix>-zeile`, `<praefix>-punkte`, `<praefix>-text` (die Schmied-Blase behält so ihre bisherigen Namen).
 */
export function baueWarteZeile(doc: Document, praefix?: string): WarteZeile {
  const mit = (basis: string, teil: string): string => (praefix ? `${basis} ${praefix}-${teil}` : basis);
  const zeile = doc.createElement("span");
  zeile.className = mit("warte-zeile", "zeile");
  const punkte = baueWartePunkte(doc, mit("warte-punkte", "punkte"));
  const text = doc.createElement("span");
  text.className = mit("warte-text", "text");
  zeile.append(punkte, text);
  return { zeile, text };
}

/** Die Status-Eigenschaften einer Warteblase: höflich, als Ganzes. */
export function alsStatus(el: HTMLElement): void {
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-atomic", "true");
}

interface Uhr {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
}

const echteUhr: Uhr = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

const obergrenzen = new WeakMap<HTMLElement, { uhr: Uhr; h: unknown }>();

/**
 * Vesperas Warteblase (noch nicht eingehängt). `name`: der Name des Bots, wenn er bekannt ist — sonst keine Namenszeile
 * (nie ein erfundener Name). Ab dem Bau läuft die Obergrenze.
 */
export function baueVesperaWarteblase(
  doc: Document,
  opt: { text: string; name?: string | null },
  uhr: Uhr = echteUhr,
): HTMLElement {
  const blase = doc.createElement("div");
  blase.className = `bubble bot is-pending ${VESPERA_WARTEBLASE_KLASSE}`;
  alsStatus(blase);
  const name = opt.name?.trim();
  if (name) {
    const n = doc.createElement("span");
    n.className = "vespera-warteblase-name";
    n.textContent = name;
    blase.append(n, " ");
  }
  const { zeile, text } = baueWarteZeile(doc);
  text.textContent = opt.text.trim();
  // Zweite, kleinere Zeile (grau): nur sichtbar, solange eine Werkzeug-Aktivität die Hauptzeile führt (setzeAktivitaet).
  const neben = doc.createElement("span");
  neben.className = "warte-neben";
  neben.hidden = true;
  blase.append(zeile, neben);
  obergrenzen.set(blase, { uhr, h: uhr.setTimeout(() => entferneWarteblase(blase), VESPERA_WARTEN_HOECHSTENS_MS) });
  return blase;
}

/** Zeigt die Nebenzeile (kleiner, grau) oder versteckt sie, wenn sie leer ist oder nur den Standardtext wiederholen würde. */
function setzeNeben(blase: HTMLElement, text: string): void {
  const neben = blase.querySelector<HTMLElement>(".warte-neben");
  if (!neben) return;
  const zeigen = text !== "" && text !== WARTE_STANDARD_TEXT;
  if (neben.textContent !== (zeigen ? text : "")) neben.textContent = zeigen ? text : "";
  neben.hidden = !zeigen;
}

/**
 * Neuer Statustext (nur wenn er sich ändert — sonst wäre jede gleiche Meldung eine neue Ansage).
 * Führt gerade eine Werkzeug-Aktivität die Hauptzeile (`setzeAktivitaet`), landet er in der Nebenzeile.
 */
export function setzeWartetext(blase: HTMLElement | null, text: string): void {
  const t = blase?.querySelector<HTMLElement>(".warte-text");
  const neu = text.trim();
  if (!blase || !t || !neu) return;
  if (blase.dataset.aktiv === "1") {
    t.dataset.basis = neu;
    setzeNeben(blase, neu);
  } else if (t.textContent !== neu) t.textContent = neu;
}

/**
 * EINE Blase für Warten UND Werkzeug-Aktivität (Martin 27.10.2026: „komisch, wenn diese beiden gleichzeitig laufen“).
 * `text` (z. B. „Durchsucht Amazon… · Schritt 3“) führt die Hauptzeile; der bisherige Wartetext rückt in die kleine graue
 * Nebenzeile (nur wenn er mehr sagt als der Standardtext). `null` stellt den Wartetext wieder her.
 */
export function setzeAktivitaet(blase: HTMLElement | null, text: string | null): void {
  const t = blase?.querySelector<HTMLElement>(".warte-text");
  if (!blase || !t) return;
  if (text === null) {
    if (blase.dataset.aktiv !== "1") return;
    delete blase.dataset.aktiv;
    t.textContent = t.dataset.basis ?? t.textContent;
    delete t.dataset.basis;
    setzeNeben(blase, "");
    return;
  }
  const neu = text.trim();
  if (!neu) return;
  if (blase.dataset.aktiv !== "1") {
    blase.dataset.aktiv = "1";
    t.dataset.basis = t.textContent ?? "";
    setzeNeben(blase, t.dataset.basis);
  }
  if (t.textContent !== neu) t.textContent = neu;
}

/** Weg damit — samt Obergrenze. Mehrfach aufrufbar. */
export function entferneWarteblase(blase: HTMLElement | null): void {
  if (!blase) return;
  const o = obergrenzen.get(blase);
  if (o) {
    o.uhr.clearTimeout(o.h);
    obergrenzen.delete(blase);
  }
  blase.remove();
}
